import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AccessContext } from '@/lib/access/access-context';
import { FALLBACK_ACCESS } from '@/lib/access/access-model';
import { getMyAccess } from '@/lib/api/capture';
import { useAuth } from '@/lib/auth/auth-context';
import type { MyAccess } from '@/lib/api/types';

/**
 * Reads `GET /capture/me` once after sign-in and shares the answer with
 * every screen (OPERATOR_ACCESS.md section 5). Sits inside `AuthProvider`,
 * where the bearer token it needs is already set, and outside
 * `RouterProvider`, so every route reads the same one fetch rather than
 * each triggering its own.
 *
 * Starts, and falls back on a failed read, at `FALLBACK_ACCESS`
 * (`lib/access/access-model.ts`): an operator standing in an aisle on a
 * flaky connection keeps the shutter while the read is in flight and keeps
 * it if the read never lands, because refusing a capture on a transient
 * error is worse than granting one for a session the API still enforces
 * its own rights on. The value resets to that same fallback on sign-out, so
 * a second operator signing in on the same device never inherits the
 * first one's answer while their own read is in flight.
 */
export function AccessProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [access, setAccess] = useState<MyAccess>(FALLBACK_ACCESS);

  useEffect(() => {
    if (status !== 'signed-in') {
      setAccess(FALLBACK_ACCESS);
      return;
    }

    let live = true;
    getMyAccess()
      .then((result) => {
        if (live) setAccess(result);
      })
      .catch((error: unknown) => {
        console.error('GET /capture/me failed; capturing stays open for this session', error);
        if (live) setAccess(FALLBACK_ACCESS);
      });
    return () => {
      live = false;
    };
  }, [status]);

  const value = useMemo(() => ({ access }), [access]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}
